<template>
  <div class="postgame">
    <div class="row justify-content-center">
      <div class="col-lg-8 col-md-10 col-12">

        <!-- ======== End time + Custom time ======== -->
        <div class="postgame-section gt-section">
          <div class="postgame-section__header">
            <div class="postgame-section__label">
              <i class="tim-icons icon-watch-time text-white"></i>
              End time
            </div>
          </div>
          <label class="postgame-field-label">True completion time</label>
          <div class="postgame-time-wrapper">
            <div class="postgame-time-group">
              <base-input class="mb-0 postgame-time-picker">
                <el-time-picker v-on:input="validateTime()"
                  v-model="model.end_time"
                  :format="picker_time_format" placeholder="HH:MM"
                ></el-time-picker>
              </base-input>
              <div class="postgame-now-append gt-now-append" @click="setNow()">
                Now
              </div>
            </div>
            <div v-if="modelValidations.end_time" class="postgame-time-error">
              {{ modelValidations.end_time }}
            </div>
          </div>

          <!-- Custom time (inline within time section) -->
          <div v-if="hasFieldEnabled(3)" class="postgame-custom-time">
            <label class="postgame-field-label">Custom time</label>
            <div class="postgame-time-wrapper">
              <base-input :value="customTimeInput" @input="onCustomTimeInput" @blur="onCustomTimeBlur" placeholder="MM:SS" inputmode="numeric" maxlength="8"></base-input>
            </div>
          </div>
        </div>

        <!-- ======== Post-event notes ======== -->
        <div class="postgame-section gt-section" v-if="hasFieldEnabled(10) || bookingSlotIdForTags">
          <div class="postgame-section__header">
            <div class="postgame-section__label">
              <i class="tim-icons icon-paper text-white"></i>
              Post-event notes
            </div>
          </div>
          <base-input v-if="hasFieldEnabled(10)">
            <textarea
              class="form-control postgame-textarea gt-textarea"
              placeholder="Start typing..."
              name="notes"
              v-model="model.post_notes"
              rows="3"></textarea>
          </base-input>
        <!-- Tags on the group's booking (same pattern as the booking drawer) -->
        <div v-if="bookingSlotIdForTags" class="postgame-custom-time">
          <label class="postgame-field-label">Tags</label>
          <div class="tags-row">
            <template v-if="tagsLoading">
              <span class="skel skel--chip"></span>
              <span class="skel skel--chip"></span>
              <span class="skel skel--chip"></span>
            </template>
            <template v-else>
            <span
              v-for="tag in bookingTags"
              :key="'TG' + tag.id"
              class="tag-chip text-white"
              :class="{ 'tag-chip--archived': tag.is_archived }"
              :style="tagChipStyle(tag.color)"
            >
              {{ tag.name }}
              <button
                class="tag-chip__remove"
                :disabled="savingTags"
                :aria-label="'Remove tag ' + tag.name"
                @click="removeTag(tag)"
              >
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </span>
            <el-dropdown trigger="click" @command="addTag">
              <span class="tag-chip tag-chip--add">
                <i v-if="savingTags" class="el-icon-loading mr-1" style="color: #e14eca;"></i>
                <i v-else class="tim-icons icon-simple-add mr-1"></i>
                Add tag
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-for="tag in availableTags" :key="'AT' + tag.id" :command="tag.id">
                  <span class="tag-chip__dot mr-1" :style="{ backgroundColor: tag.color }"></span>
                  {{ tag.name }}
                </el-dropdown-item>
                <el-dropdown-item v-if="availableTags.length === 0" disabled>No more tags</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            </template>
          </div>
        </div>

        </div>

        <!-- ======== Completed ======== -->
        <div class="postgame-section gt-section" v-if="hasFieldEnabled(6)">
          <div class="postgame-section__header">
            <div class="postgame-section__label">
              <i class="tim-icons icon-trophy text-white"></i>
              Did the group complete the event?
            </div>
          </div>
          <div class="completed-options">
            <div
              class="completed-option"
              :class="{ 'completed-option--active': model.completed === true || model.completed === 'true' }"
              @click="model.completed = true"
            >
              <i class="tim-icons icon-check-2"></i>
              <span>Yes</span>
            </div>
            <div
              class="completed-option"
              :class="{ 'completed-option--active completed-option--no': model.completed === false || model.completed === 'false' }"
              @click="model.completed = false"
            >
              <i class="tim-icons icon-simple-remove"></i>
              <span>No</span>
            </div>
          </div>
        </div>

        <!-- ======== Host ======== -->
        <div class="postgame-section gt-section" v-if="hostFieldEnabled">
          <div class="postgame-section__header">
            <div class="postgame-section__label">
              <i class="tim-icons icon-single-02 text-white"></i>
              Host
            </div>
          </div>
          <base-input class="mb-0">
            <el-select v-model="hostId" class="select-primary gt-select w-100" placeholder="Host">
              <el-option v-for="s in hostOptions" :key="'H' + s.id" class="select-primary"
                         :label="staffName(s)" :value="s.id"></el-option>
            </el-select>
          </base-input>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  import { BaseRadio } from 'src/components/index';
  import { groupTimeDigits, formatCustomTime, toStoredCustomTime } from "@/util/customTime";
  import {API_LOCATION} from "@/api-config";
  import axios from "axios";
  import {Auth} from "aws-amplify";
  import {TimePicker, Dropdown, DropdownMenu, DropdownItem} from "element-ui";
  import { tagChipStyle } from '@/util/tagChip'
  import { defaultHostId } from "@/util/trackerHost";
  import swal from "sweetalert2";

  export default {
      name: "PostGameNotesStep",
      props: {
        gameInstanceId: { type: [String, Number], default: null }
      },
      components: {
        BaseRadio,
        [TimePicker.name]: TimePicker,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
      },
      data() {
        return {
          draftPopulated: false,
          groupTags: [],
          bookingTags: [],
          savingTags: false,
          tagsLoading: false,
          bookingSlotIdForTags: null,
          customTimeInput: '',
          optional_fields: [],
          hostId: null,
          hostOptions: [],
          hostDefaulted: false,
          picker_time_format: 'hh:mm A',
          time_format: JSON.parse(localStorage.getItem('group')).time_format,
          model: {
            end_time: '',
            post_notes: '',
            completed: true,
            custom_time: ''
          },
          dd: 'AM',
          hh: '',
          mm: '',
          modelValidations: {
            end_time: '',
            pre_notes: {
              required: false,
            },
            team_experience: {
              required: false,
            },
          }
        }
      },
      computed: {
        hostFieldEnabled() {
          return (this.optional_fields || []).some(f => f.column_name === 'host' && f.is_enabled)
        },
        hostName() {
          const s = this.hostOptions.find(o => Number(o.id) === Number(this.hostId))
          return s ? this.staffName(s) : ''
        },
        availableTags() {
          const applied = new Set(this.bookingTags.map(t => t.id))
          return this.groupTags.filter(t => !applied.has(t.id))
        }
      },
      methods: {
        tagChipStyle,
        tagsBaseUrl() {
          let companyId = localStorage.getItem('companyId')
          let companyGroupId = JSON.parse(localStorage.getItem('group')).id
          return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        },
        loadTags() {
          // Booking id comes from the saved booking-info draft; unbooked
          // games have none and the section stays hidden.
          this.bookingSlotIdForTags = null
          this.bookingTags = []
          try {
            let info = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo')))
            if (info && info.booking && info.booking.id) {
              this.bookingSlotIdForTags = info.booking.id
            }
          } catch (e) {
            // no booking info saved
          }
          if (this.bookingSlotIdForTags) {
            this.fetchTags()
          }
        },
        async fetchTags() {
          if (!this.bookingSlotIdForTags) return
          this.tagsLoading = true
          try {
            let token = await this.getAuthToken()
            let config = { headers: { Authorization: token } }
            let catalog = await axios.get(this.tagsBaseUrl() + '/tags', config)
            this.groupTags = catalog.data || []
            let applied = await axios.get(this.tagsBaseUrl() + '/bookings/' + this.bookingSlotIdForTags + '/tags', config)
            this.bookingTags = applied.data || []
          } catch (e) {
            console.log('Tags not available:', e.message)
            this.groupTags = []
            this.bookingTags = []
          } finally {
            this.tagsLoading = false
          }
        },
        async saveTagSet(ids) {
          if (this.savingTags || !this.bookingSlotIdForTags) return
          this.savingTags = true
          let previous = this.bookingTags
          try {
            let token = await this.getAuthToken()
            let config = { headers: { Authorization: token } }
            let url = this.tagsBaseUrl() + '/bookings/' + this.bookingSlotIdForTags + '/tags'
            let response = await axios.put(url, { tag_ids: ids }, config)
            this.bookingTags = response.data.tags || []
          } catch (e) {
            this.bookingTags = previous
            this.$notify({ type: 'danger', message: 'Failed to update tags', timeout: 3000 })
          } finally {
            this.savingTags = false
          }
        },
        addTag(tagId) {
          if (this.bookingTags.some(t => t.id === tagId)) return
          this.saveTagSet([...this.bookingTags.map(t => t.id), tagId])
        },
        removeTag(tag) {
          this.saveTagSet(this.bookingTags.map(t => t.id).filter(id => id !== tag.id))
        },
        draftKey(key) {
          if (!this.gameInstanceId) {
            console.warn('draftKey called with no gameInstanceId for key:', key)
            return key + '_NONE'
          }
          return key + '_' + this.gameInstanceId
        },
        setOptionalFields(fields){
          this.optional_fields = fields
        },
        hasFieldEnabled(fieldId){
          for(let n in this.optional_fields){
            if(this.optional_fields[n].field_id === fieldId && this.optional_fields[n].is_enabled){
              return true
            }
          }
          return false
        },
        staffName(s) {
          if (!s.first_name && !s.last_name) return s.email || 'Unknown'
          return [s.first_name, s.last_name].filter(Boolean).join(' ')
        },
        async loadHostOptions() {
          if (!this.hostFieldEnabled) return
          if (this.hostOptions.length === 0) {
            try {
              const token = await this.getAuthToken()
              const config = { headers: { Authorization: token } }
              const companyId = localStorage.getItem('companyId')
              const companyGroupId = JSON.parse(localStorage.getItem('group')).id
              const response = await axios.get(API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/company-users', config)
              this.hostOptions = response.data.company_users || []
            } catch (e) {
              console.log('Host options not available:', e.message)
              return
            }
          }
          if (!this.hostDefaulted) {
            const profile = JSON.parse(localStorage.getItem('profile') || 'null')
            this.hostId = defaultHostId([], this.hostOptions, profile ? profile.id : null)
            this.hostDefaulted = true
          }
        },
        validateTime(){
          let today = new Date()
          this.model.end_time.setMonth(today.getMonth(), today.getDate())
        },
        resetState(){
          this.draftPopulated = false
          this.model.end_time = ''
          this.model.post_notes = ''
          this.model.completed = true
          this.model.custom_time = ''
          this.customTimeInput = ''
          this.bookingTags = []
          this.bookingSlotIdForTags = null
          this.hostId = null
          this.hostDefaulted = false
        },
        onCustomTimeInput(val){
          // model.custom_time stays canonical HH:MM:SS; the field shows the grouped form
          this.customTimeInput = groupTimeDigits(val)
          this.model.custom_time = toStoredCustomTime(this.customTimeInput)
        },
        onCustomTimeBlur(){
          this.customTimeInput = formatCustomTime(this.model.custom_time)
        },
        populateDraft(){
          this.draftPopulated = true
          this.loadTags()
          this.loadHostOptions()
          let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
          if(draft){
            // Check both actual_end_time (from localStorage) and end_time (from backend)
            const endTimeValue = draft.actual_end_time || draft.end_time;

            if(endTimeValue && endTimeValue[0] != 0){
              let time;

              // Handle both MySQL format "2025-11-21 19:16:00" and ISO format "2025-11-21T19:16:00.000Z"
              if(typeof endTimeValue === 'string') {
                // If it contains 'T', it's ISO format
                if(endTimeValue.includes('T')) {
                  time = new Date(endTimeValue);
                  time.setHours(endTimeValue.split('T')[1].split(':')[0]);
                  time.setMinutes(endTimeValue.split('T')[1].split(':')[1]);
                } else {
                  // MySQL format "2025-11-21 19:16:00" - parse manually to preserve local time
                  const parts = endTimeValue.split(' ');
                  if(parts.length >= 2) {
                    const dateParts = parts[0].split('-'); // [2025, 11, 21]
                    const timeParts = parts[1].split(':'); // [19, 16, 00]

                    // Create Date in local timezone
                    time = new Date(
                      parseInt(dateParts[0]), // year
                      parseInt(dateParts[1]) - 1, // month (0-indexed)
                      parseInt(dateParts[2]), // day
                      parseInt(timeParts[0]), // hours
                      parseInt(timeParts[1]), // minutes
                      parseInt(timeParts[2] || 0) // seconds
                    );
                  } else {
                    time = null;
                  }
                }
              } else {
                // Fallback: already a Date object or null
                time = endTimeValue;
              }

              this.model.end_time = time;
            } else {
              this.model.end_time = null
            }

            if(draft.hasOwnProperty('custom_time')){
              this.model.custom_time = draft.custom_time
              this.customTimeInput = formatCustomTime(draft.custom_time)
            }

            this.model.completed = draft.completed === true || draft.completed === 'true' || draft.completed === 1
            this.model.post_notes = draft.post_game_notes
          }
        },
        setNow () {
          this.model.end_time = new Date()
        },
        setAutoEndTime(endTime) {
          // Set the end time from the Complete Game button
          this.model.end_time = endTime;
        },
        getAuthToken() {
          return new Promise ( function(resolve) {
            Auth.currentSession()
              .then(data =>{
                resolve(data.idToken.jwtToken)
              })
              .catch(err => console.log(err))
          })
        },
        async setPostNotes () {
          if(!this.model.end_time || this.model.end_time === "NaN:"){
            this.modelValidations.end_time = 'End time cannot be empty'
            return
          } else {
            this.modelValidations.end_time = ''
          }

          localStorage.setItem(this.draftKey('postNotes'), JSON.stringify(this.model))
          //save draft
          let companyId = localStorage.getItem('companyId')
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId
            + '/games/instances/' + this.gameInstanceId + '/drafts'

          // this.convertTime()
          let data = {
            end_time: this.model.end_time.getFullYear() + '-' + (this.model.end_time.getMonth()+1) + '-' + this.model.end_time.getDate() +
              ' ' + this.model.end_time.getHours() + ':' + this.model.end_time.getMinutes(),
            post_game_notes: this.model.post_notes,
            completed: this.model.completed,
            custom_time: this.model.custom_time
          }
          axios.put(url, data, config)
            .then(response => {
              //update LS draft
              let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
              draft.actual_end_time = this.model.end_time.getFullYear() + '-' +
                ('0' + (this.model.end_time.getMonth()+1)).slice(-2) + '-' + ('0' + this.model.end_time.getDate()).slice(-2) +
                'T' + ('0' + (this.model.end_time.getHours())).slice(-2) + ':' + ('0' + (this.model.end_time.getMinutes())).slice(-2) + ':00.000Z'
              draft.post_game_notes = data.post_game_notes
              draft.completed = data.completed
              draft.custom_time = data.custom_time
              localStorage.setItem(this.draftKey('draft'), JSON.stringify(draft))
            })
            .catch(err => {
              if(err.response && err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              } else if(err.response && err.response.status == 409){
                // Without this the notes silently fail to save and the local
                // draft still shows them, so the host believes they were kept.
                swal({
                  title: 'This event form has already been submitted',
                  text: "These notes were not saved. Open the event from event history to make changes, or start a fresh tracker for the event you are running.",
                  type: 'error',
                  confirmButtonColor: '#1d8cf8',
                  confirmButtonText: 'OK'
                })
              }
            })
        },
        async saveDraftToBackend() {
          this.saveToLocalDraft()
          if (!this.gameInstanceId) return
          if (!this.model.end_time || !(this.model.end_time instanceof Date)) return

          let companyId = localStorage.getItem('companyId')
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId
            + '/games/instances/' + this.gameInstanceId + '/drafts'

          let data = {
            end_time: this.model.end_time.getFullYear() + '-' + (this.model.end_time.getMonth()+1) + '-' + this.model.end_time.getDate() +
              ' ' + this.model.end_time.getHours() + ':' + this.model.end_time.getMinutes(),
            post_game_notes: this.model.post_notes,
            completed: this.model.completed,
            custom_time: this.model.custom_time
          }
          return axios.put(url, data, config).catch(err => {
            console.log('Background draft save failed:', err)
          })
        },
        saveToLocalDraft() {
          let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
          if (!draft) return
          if (this.model.end_time && this.model.end_time instanceof Date) {
            draft.actual_end_time = this.model.end_time.getFullYear() + '-' +
              ('0' + (this.model.end_time.getMonth()+1)).slice(-2) + '-' + ('0' + this.model.end_time.getDate()).slice(-2) +
              'T' + ('0' + (this.model.end_time.getHours())).slice(-2) + ':' + ('0' + (this.model.end_time.getMinutes())).slice(-2) + ':00.000Z'
          }
          draft.post_game_notes = this.model.post_notes
          draft.completed = this.model.completed
          draft.custom_time = this.model.custom_time
          localStorage.setItem(this.draftKey('draft'), JSON.stringify(draft))
        },
        async validate() {
          if(!this.model.end_time || this.model.end_time === "NaN:"){
            this.modelValidations.end_time = 'End time cannot be empty'
            return
          } else {
            this.modelValidations.end_time = ''
          }
          this.$emit('on-validated', true, this.model);
          return true;
        }
      },
      mounted() {
        if (this.time_format === 'HH:mm') {
          this.picker_time_format = 'HH:mm'
        }
      }
    }
</script>

<style scoped>

/* ========================================
   Layout
   ======================================== */
.postgame {
  padding-top: 8px;
}

/* ========================================
   Section containers — visual base from gt-section
   ======================================== */
.postgame-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.postgame-section__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
}

.postgame-section__label i {
  font-size: 14px;
}


/* ========================================
   End time
   ======================================== */
.postgame-time-wrapper {
  max-width: 260px;
}

.postgame-time-group {
  display: flex;
  align-items: stretch;
}

.postgame-time-group .postgame-time-picker {
  flex: 1;
}

.postgame-time-error {
  color: #ec250d;
  font-size: 12px;
  margin-top: 4px;
}

/* ========================================
   Field labels
   ======================================== */
.postgame-field-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 4px;
}

/* ========================================
   Custom time (inside time section)
   ======================================== */
.postgame-custom-time {
  margin-top: 12px;
}

.postgame-custom-time .postgame-time-wrapper {
  max-width: 140px;
}

/* Textarea — visual base from gt-textarea */

/* ========================================
   Completed options
   ======================================== */
.completed-options {
  display: flex;
  gap: 8px;
  max-width: 220px;
}

.completed-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.15s ease;
}

.completed-option i {
  font-size: 12px;
}

.completed-option:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.85);
}

.completed-option--active {
  border-color: #00bf9a;
  background: rgba(0, 191, 154, 0.06);
  color: #00bf9a;
}

.completed-option--active:hover {
  background: rgba(0, 191, 154, 0.1);
  color: #00bf9a;
}

.completed-option--no {
  border-color: #fd5d93;
  background: rgba(253, 93, 147, 0.06);
  color: #fd5d93;
}

.completed-option--no:hover {
  background: rgba(253, 93, 147, 0.1);
  color: #fd5d93;
}

/* ========================================
   Responsive
   ======================================== */
@media (max-width: 576px) {
  .postgame-section {
    padding: 14px;
  }

  .postgame-time-wrapper {
    max-width: 100%;
  }
}
</style>

<!-- Light mode overrides (unscoped) — section base handled by gt-section -->
<style>
.white-content .postgame-section__label {
  color: #344675;
}

.white-content .postgame-section__label i {
  color: #344675;
}


.white-content .postgame-field-label {
  color: rgba(0, 0, 0, 0.45);
}

/* Textarea light mode handled by gt-textarea */

.white-content .completed-option {
  background: rgba(0, 0, 0, 0.01);
  border-color: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.4);
}

.white-content .completed-option:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.7);
}

.white-content .completed-option--active {
  border-color: #00bf9a;
  background: rgba(0, 191, 154, 0.04);
  color: #00bf9a;
}

.white-content .completed-option--active:hover {
  background: rgba(0, 191, 154, 0.08);
  color: #00bf9a;
}

.white-content .completed-option--no {
  border-color: #fd5d93;
  background: rgba(253, 93, 147, 0.04);
  color: #fd5d93;
}

.white-content .completed-option--no:hover {
  background: rgba(253, 93, 147, 0.08);
  color: #fd5d93;
}

/* Time picker input group overrides (must be unscoped for Element UI internals) */
.postgame-time-group .postgame-time-picker .el-date-editor {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.postgame-time-group .postgame-time-picker .el-input__inner,
.postgame-time-group .postgame-time-picker .form-control {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
}
</style>
